import { useCallback, useMemo, useState } from 'react';
import { PillButton } from '@ab-platform/ui/pill-button';
import {
  confirmOnboardingDocumentUpload,
  signOnboardingDocumentUpload,
  uploadFileToSignedUrl,
  type UploadDocumentType,
  type UploadErrorCode,
  type UploadMimeType,
} from '@/lib/api';
import {
  DocumentDropCard,
  type DocumentSlotConfig,
  type DocumentSlotState,
} from '@/components/DocumentDropCard';
import { useWizardSessionIdContext } from '@/hooks/WizardSessionIdContext';

type LegalNature = 'PERSON' | 'COMPANY';
type OrgType = 'BROKER' | 'AGENZIA' | 'INTERMEDIARIO';

const ALLOWED_MIME_TYPES: ReadonlyArray<UploadMimeType> = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const INITIAL_SLOT: DocumentSlotState = { status: 'idle' };

/**
 * Compute which slots to render based on legal nature + orgType.
 *
 * Rules (PR-ONBOARDING-IVASS-DOCS-DIDIT-REALIGN-1):
 *   - VISURA_CAMERALE   → COMPANY only (required)
 *   - RC_PROFESSIONALE  → BROKER + AGENZIA only (required); esclusa per
 *                         orgType=INTERMEDIARIO (Collab E).
 *
 * NON più upload manuali (minimizzazione dati GDPR):
 *   - DOCUMENTO_IDENTITA / CODICE_FISCALE → identità e codice fiscale sono
 *     coperti dalla verifica d'identità (Didit/KYC). Il CF resta un dato
 *     DICHIARATO obbligatorio nel form; non si caricano carta d'identità né
 *     tessera sanitaria.
 *
 * Può restituire un array VUOTO (es. INTERMEDIARIO + PERSON → nessun upload
 * manuale richiesto).
 */
function buildSlotConfigs(
  legalNature: LegalNature,
  orgType: OrgType,
): ReadonlyArray<DocumentSlotConfig> {
  const base: DocumentSlotConfig[] = [];
  if (legalNature === 'COMPANY') {
    base.push({
      type: 'VISURA_CAMERALE',
      label: 'Visura camerale',
      description: 'Estratto del registro imprese (max 6 mesi).',
      required: true,
    });
  }
  if (orgType === 'BROKER' || orgType === 'AGENZIA') {
    base.push({
      type: 'RC_PROFESSIONALE',
      label: 'Polizza RC professionale',
      description:
        'Copia della polizza in corso di validità (obbligatoria per agenzie e broker).',
      required: true,
    });
  }
  return base;
}

export interface DocumentUploadStepProps {
  uploadToken: string;
  legalNature: LegalNature;
  orgType: OrgType;
  onAllRequiredUploaded: () => void;
  onSessionExpired: () => void;
}

export function DocumentUploadStep({
  uploadToken,
  legalNature,
  orgType,
  onAllRequiredUploaded,
  onSessionExpired,
}: DocumentUploadStepProps): JSX.Element {
  const wizardSessionId = useWizardSessionIdContext();
  const slots = useMemo(
    () => buildSlotConfigs(legalNature, orgType),
    [legalNature, orgType],
  );
  const [state, setState] = useState<Record<UploadDocumentType, DocumentSlotState>>(
    () => ({
      DOCUMENTO_IDENTITA: INITIAL_SLOT,
      CODICE_FISCALE: INITIAL_SLOT,
      VISURA_CAMERALE: INITIAL_SLOT,
      RC_PROFESSIONALE: INITIAL_SLOT,
    }),
  );

  const allRequiredUploaded = slots
    .filter((s) => s.required)
    .every((s) => state[s.type].status === 'uploaded');

  const anyUploading = slots.some((s) => state[s.type].status === 'uploading');

  const updateSlot = useCallback(
    (type: UploadDocumentType, patch: Partial<DocumentSlotState>) => {
      setState((prev) => ({
        ...prev,
        [type]: { ...prev[type], ...patch },
      }));
    },
    [],
  );

  const handleFileSelected = useCallback(
    async (config: DocumentSlotConfig, file: File): Promise<void> => {
      // Client-side guards so the user sees the error inline before the
      // round-trip to the BE / GCS. Same pipeline for click-picker and
      // drag-drop paths.
      if (!ALLOWED_MIME_TYPES.includes(file.type as UploadMimeType)) {
        updateSlot(config.type, {
          status: 'error',
          errorMessage:
            'Formato non supportato. Carica un PDF, JPEG o PNG.',
          filename: file.name,
          sizeBytes: file.size,
        });
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        updateSlot(config.type, {
          status: 'error',
          errorMessage: 'File troppo grande. Massimo 10 MB.',
          filename: file.name,
          sizeBytes: file.size,
        });
        return;
      }

      updateSlot(config.type, {
        status: 'uploading',
        filename: file.name,
        sizeBytes: file.size,
        errorMessage: undefined,
        documentId: undefined,
      });

      const signed = await signOnboardingDocumentUpload({
        uploadToken,
        documentType: config.type,
        originalFilename: file.name,
        mimeType: file.type as UploadMimeType,
        sizeBytes: file.size,
        wizardSessionId: wizardSessionId || undefined,
      });
      if (!signed.ok) {
        if (signed.code === 'UPLOAD_TOKEN_INVALID') {
          onSessionExpired();
          return;
        }
        updateSlot(config.type, {
          status: 'error',
          errorMessage: messageForUploadError(signed.code),
        });
        return;
      }

      const put = await uploadFileToSignedUrl(
        signed.data.signedUrl,
        file,
        file.type,
      );
      if (!put.ok) {
        updateSlot(config.type, {
          status: 'error',
          errorMessage:
            'Caricamento del file non riuscito. Verifica la connessione e riprova.',
        });
        return;
      }

      const confirmed = await confirmOnboardingDocumentUpload(
        signed.data.documentId,
        uploadToken,
        wizardSessionId || undefined,
      );
      if (!confirmed.ok) {
        if (confirmed.code === 'UPLOAD_TOKEN_INVALID') {
          onSessionExpired();
          return;
        }
        updateSlot(config.type, {
          status: 'error',
          errorMessage: messageForUploadError(confirmed.code),
        });
        return;
      }

      updateSlot(config.type, {
        status: 'uploaded',
        documentId: signed.data.documentId,
        errorMessage: undefined,
      });
    },
    [onSessionExpired, updateSlot, uploadToken, wizardSessionId],
  );

  const handleReset = useCallback(
    (type: UploadDocumentType) => {
      updateSlot(type, INITIAL_SLOT);
    },
    [updateSlot],
  );

  const hasSlots = slots.length > 0;

  return (
    <div className="grid gap-5">
      <p className="text-sm text-muted-foreground">
        {hasSlots
          ? 'Allega i documenti richiesti per completare la richiesta. Trascina il file sulla riga del documento corretto, oppure clicca sulla card per scegliere il file. Formati accettati: PDF, JPEG, PNG. Dimensione massima: 10 MB.'
          : 'Per questo profilo non sono richiesti documenti da caricare manualmente. Identità e codice fiscale sono verificati tramite la verifica d’identità; il numero RUI è verificato sul Registro Pubblico IVASS. Puoi inviare la richiesta.'}
      </p>

      <ul className="grid gap-4">
        {slots.map((config) => (
          <DocumentDropCard
            key={config.type}
            config={config}
            slot={state[config.type]}
            disabled={anyUploading && state[config.type].status !== 'uploading'}
            acceptedMimeTypes={ALLOWED_MIME_TYPES}
            maxSizeBytes={MAX_SIZE_BYTES}
            onSelect={(file) => {
              void handleFileSelected(config, file);
            }}
            onReset={() => handleReset(config.type)}
          />
        ))}
      </ul>

      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          {!hasSlots
            ? 'Nessun documento da caricare per questo profilo.'
            : allRequiredUploaded
              ? 'Tutti i documenti obbligatori sono stati caricati.'
              : 'Carica tutti i documenti contrassegnati come obbligatori per inviare la richiesta.'}
        </p>
        <PillButton
          type="button"
          disabled={!allRequiredUploaded || anyUploading}
          onClick={onAllRequiredUploaded}
        >
          Invia richiesta
        </PillButton>
      </div>
    </div>
  );
}

function messageForUploadError(code: UploadErrorCode): string {
  switch (code) {
    case 'VALIDATION_FAILED':
      return 'Il file non rispetta i requisiti. Controlla formato e dimensione.';
    case 'DOCUMENT_NOT_FOUND':
      return 'Il documento non è stato trovato. Riprova.';
    case 'UPLOAD_NOT_RECEIVED':
      return 'Il file non è stato ricevuto. Riprova.';
    case 'RATE_LIMITED':
      return 'Troppe richieste, attendi qualche istante e riprova.';
    case 'NETWORK_ERROR':
      return 'Problema di connessione. Riprova.';
    case 'UPLOAD_TOKEN_INVALID':
      // Handled at the call site (onSessionExpired); the message is a
      // defensive fallback that should never render in practice.
      return 'Sessione scaduta. Invia nuovamente la richiesta.';
    case 'UNKNOWN':
    default:
      return 'Si è verificato un errore. Riprova.';
  }
}
